import { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { useRequestProcessor } from "../../requestProcessor";
import { App } from "@capacitor/app";
import { ReloadData } from "../../queryfunctions/queryfunctions";
import { startRequestBlocking, setReloadTrigger } from "../../axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { getSurvey } from "../../atomstore/serviceAtoms";
import { survey_id } from "../../atomstore/store";

const Reload = () => {
    const location = useLocation()
    const navigate = useNavigate();
    const [surveyId] = useAtom(survey_id);
    const { useMutationWrapper } = useRequestProcessor();
    const queryClient = useQueryClient();

    // Prevents concurrent/duplicate reload calls
    const isReloadingRef = useRef(false);
    // Holds the listener handle — ref so async cleanup always finds it
    const listenerHandleRef = useRef(null);
    // Always points to the latest handleReload closure so the listener never goes stale
    const handleReloadRef = useRef(null);

    const redirectToSurveyIfNeeded = () => {
        const survey = getSurvey();
        const surveyNum = Number(survey);
        if (surveyNum > 0 && !location.pathname.includes("/survey")) {
            console.log("[Reload] Redirecting to survey:", surveyNum);
            navigate("/survey", { replace: true });
        }
    };

    const reloadData = useMutationWrapper(
        "reload",
        () => ReloadData(),
        {
            onMutate: () => {
                // Cancel all ongoing React Query queries when reload starts
                // New queries will be blocked by axios interceptor
                console.log("[Reload] Cancelling React Query queries during reload");
                queryClient.cancelQueries();
            },
            onSuccess: (data) => {
                console.log("[Reload] Reload child data response:", data);
            },
            onSettled: () => {
                // Queries will automatically retry after reload completes
                // The axios interceptor will allow them through now
                console.log("[Reload] Reload completed, queries can now proceed");
                isReloadingRef.current = false;
                // ReloadData sets survey_id but does not navigate — show survey immediately
                redirectToSurveyIfNeeded();
            },
        }
    );

    // -----------------------------
    // DATE CHECK
    // -----------------------------
    const check_date = (dateString) => {
        if (!dateString) return false;

        try {
            dateString = JSON.parse(dateString);
        } catch {
            console.log("Invalid stored date:", dateString);
            return false;
        }

        console.log("Checking date => ", dateString);

        const givenDate = new Date(dateString);
        const today = new Date();

        // Normalize both to date-only (ignore hours)
        givenDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        console.log("Given Date => ", givenDate, " === ", today);

        return givenDate.getTime() === today.getTime();
    };

    // -----------------------------
    // MAIN RELOAD LOGIC
    // -----------------------------
    // force=true bypasses the date check — used by the response interceptor when
    // AUTH_EXPIRED is detected on a response that slipped past the request interceptor.
    const handleReload = async (force = false) => {
        if (isReloadingRef.current) {
            console.log("[Reload] Already reloading — skipping duplicate call.");
            return;
        }

        console.log("[Reload] Running reload check on app start or resume...");

        const token = localStorage.getItem("auth_token");
        const reloadTime = localStorage.getItem("last_reload_time");
        const survey = getSurvey();

        console.log("[Reload] Auth Token:", token);
        console.log("[Reload] Reload Time:", reloadTime);
        console.log("[Reload] Survey ID:", survey);
        console.log("[Reload] Force:", force);

        if (!force && !reloadTime) {
            console.log("[Reload] No last_reload_time found. Skipping reload.");
            return;
        }

        if (token && token !== '""') {
            const shouldReload = force || !check_date(reloadTime);

            if (shouldReload) {
                console.log("[Reload] Blocking requests and reloading data...");
                isReloadingRef.current = true;
                // Block requests synchronously NOW — before the async mutation fires.
                // Covers the app-resume case where components may re-fetch in the same tick.
                startRequestBlocking();
                reloadData.mutate();
            } else {
                console.log("[Reload] No reload needed. Checking survey. survey:", survey);
                redirectToSurveyIfNeeded();
            }
        } else {
            console.log("[Reload] No auth token. Removing listeners.");
            await App.removeAllListeners();
        }
    };

    // Keep ref in sync every render so the listener always calls the latest closure
    handleReloadRef.current = handleReload;

    // React to survey_id from any ReloadData caller (Profile, CCTV, daily reload, etc.)
    useEffect(() => {
        if (Number(surveyId) > 0 && !location.pathname.includes("/survey")) {
            console.log("[Reload] survey_id changed — redirecting to survey:", surveyId);
            navigate("/survey", { replace: true });
        }
    }, [surveyId, location.pathname, navigate]);

    // -----------------------------
    // SETUP LISTENER (Capacitor v5 fix)
    // -----------------------------
    useEffect(() => {
        console.log("[Reload] Setting up appStateChange listener...");

        let cancelled = false;

        // Register the force-reload callback so the axios response interceptor can
        // trigger a silent reload+retry when AUTH_EXPIRED reaches the response layer.
        setReloadTrigger((force) => handleReloadRef.current?.(force));

        // Use the ref so the callback always has the latest handleReload,
        // avoiding stale closure issues across re-renders.
        App.addListener("appStateChange", ({ isActive }) => {
            console.log("[Reload] appStateChange =>", isActive);
            if (isActive) handleReloadRef.current?.();
        }).then((handle) => {
            if (cancelled) {
                // Component unmounted before the promise resolved — remove immediately
                handle.remove();
            } else {
                listenerHandleRef.current = handle;
            }
        });

        handleReloadRef.current?.();

        return () => {
            cancelled = true;
            console.log("[Reload] Cleaning up appStateChange listener...");
            setReloadTrigger(null);
            if (listenerHandleRef.current) {
                listenerHandleRef.current.remove();
                listenerHandleRef.current = null;
            }
        };
    }, []);

    return null;
};

export default Reload;
